import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { SAVING_ARENA_API_CONFIG } from '../api/api.config';
import { AnalyticsService } from '../analytics/analytics.service';

/** What a click-out needs to know about itself, for the GA4 event. */
export interface ClickOutContext {
  /** Store name as a human would read it — this is what the report is grouped by. */
  store: string;
  /** Which surface the click came from, so the same store can be compared across pages. */
  kind?: string;
}

/**
 * Every outbound affiliate link on the storefront.
 *
 * Seventeen call sites used to build `${assetBaseUrl}/go/...` inline, each also deciding its own
 * `rel`, whether to fire a GA4 event, and in what order to open the second tab. They disagreed:
 * two surfaces shipped the wrong `rel`, and only two of the nine that navigate fired `click_out`
 * at all. Centralising the URL is the small half of this; centralising the *behaviour* is the point.
 *
 * The split between the two groups of methods below is deliberate and load-bearing.
 *
 *   `*Url()` returns an href. It is safe to render during SSR, produces the same string on the
 *   server and in the browser, and NEVER appends `?ref=`.
 *
 *   `open*()` performs a click. It is browser-only, appends `?ref=`, fires the analytics event, and
 *   owns the ordering that the navigation depends on.
 *
 * `?ref=` is what forced the split. The store logo used to append it only in the browser, so the
 * server-rendered href and the hydrated one differed — a hydration mismatch Angular does not warn
 * about for attribute values, and the practical effect was that a click landing before hydration
 * recorded no landing path while one after it did. `landingPath` therefore under-reported on slow
 * devices, which is exactly the skew that makes a metric untrustworthy. An href that is identical
 * on both sides cannot do that, and it also removes the last way the server and browser markup can
 * diverge — which matters for the no-cloaking guarantee, since Googlebot reads the SSR output.
 *
 * The cost is that a real `<a href>` click-out (the store logo, the product card) carries no
 * `landingPath`. The redirect still records the `Referer`, which identifies the same page. The
 * alternative — a click handler rewriting `location.href` — would break middle-click and
 * ctrl-click on an anchor, which is a worse trade for a metric we can get another way.
 */
@Injectable({ providedIn: 'root' })
export class AffiliateLinkService {
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly analytics = inject(AnalyticsService);
  private readonly document = inject(DOCUMENT);

  /**
   * The one `rel` for every affiliate anchor on the site.
   *
   * `sponsored` because that is what an affiliate link is and Google's link-spam policy requires it
   * to be declared; `nofollow` alongside it because older crawlers do not understand `sponsored`;
   * `noopener` because the new tab must not get a handle back to this window.
   *
   * Deliberately no `noreferrer`: it suppresses the `Referer` header, and the redirect records that
   * header as the click's origin. Two surfaces used to carry it and were silently blanking a field
   * we collect.
   */
  readonly rel = 'nofollow sponsored noopener';

  /** `/go/s/:storeSlug` — the store itself, as on a store page's logo. */
  storeUrl(slug: string): string {
    const value = (slug ?? '').trim();
    return value ? `${this.origin()}/go/s/${encodeURIComponent(value)}` : '';
  }

  /** `/go/:storeSlug/:couponId` — a coupon or deal. The slug is decorative; the id resolves it. */
  couponUrl(storeSlug: string, couponId: string): string {
    const slug = (storeSlug ?? '').trim();
    const id = (couponId ?? '').trim();
    return slug && id ? `${this.origin()}/go/${encodeURIComponent(slug)}/${encodeURIComponent(id)}` : '';
  }

  /** `/go/p/:productId` — a product's "Buy Now". */
  productUrl(productId: string): string {
    const id = (productId ?? '').trim();
    return id ? `${this.origin()}/go/p/${encodeURIComponent(id)}` : '';
  }

  /**
   * Leave for a merchant on a coupon or deal.
   *
   * Opens the coupon's own page in a second tab first, so the visitor still has the code to copy
   * when they come back, then sends this tab to the merchant.
   */
  openCoupon(options: ClickOutContext & { storeSlug: string; couponId: string; couponPath?: string }): void {
    const url = this.couponUrl(options.storeSlug, options.couponId);
    if (!url) return;

    const path = options.couponPath
      ?? `/${options.storeSlug}/coupon/${encodeURIComponent(options.couponId)}`;

    this.leave(url, {
      store: options.store,
      kind: options.kind ?? 'coupon',
      offerId: options.couponId,
      newTab: path,
    });
  }

  /** Leave for a merchant on a product. No second tab: there is no code to come back for. */
  openProduct(options: ClickOutContext & { productId: string }): void {
    const url = this.productUrl(options.productId);
    if (!url) return;

    this.leave(url, { store: options.store, kind: options.kind ?? 'product', offerId: options.productId });
  }

  /** Leave for a merchant on the store itself. */
  openStore(options: ClickOutContext & { slug: string }): void {
    const url = this.storeUrl(options.slug);
    if (!url) return;

    this.leave(url, { store: options.store, kind: options.kind ?? 'store', offerId: '' });
  }

  private origin(): string {
    return this.apiConfig.assetBaseUrl.replace(/\/$/, '');
  }

  /**
   * The three steps of a click-out, in the order the navigation requires.
   *
   * The second tab opens first, because a popup blocker only allows it while the original user
   * gesture is still on the stack. The analytics event goes next, BEFORE the assignment below
   * starts unloading this page — `AnalyticsService` sends it over `sendBeacon` for exactly that
   * reason, since an ordinary XHR is cancelled mid-flight by the navigation. Getting this order
   * wrong loses either the tab or the event, silently, and it used to be re-derived at every call
   * site.
   */
  private leave(
    url: string,
    details: { store: string; kind: string; offerId: string; newTab?: string },
  ): void {
    const win = this.document.defaultView;
    if (!win) return;

    if (details.newTab) win.open(details.newTab, '_blank', 'noopener');

    this.analytics.trackClickOut({
      store: details.store,
      offerId: details.offerId,
      kind: details.kind,
    });

    win.location.href = this.withRef(url, win.location.pathname);
  }

  /** Where the click came from, for the redirect's `landingPath`. Browser-only by construction. */
  private withRef(url: string, pathname: string): string {
    if (!pathname) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}ref=${encodeURIComponent(pathname)}`;
  }
}
